import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import type { CredentialResponse } from '@react-oauth/google';
import {
    CheckCircle, AlertCircle, Copy, CreditCard, Check, ArrowLeft,
    MessageCircle, ShieldCheck, Sparkles, Plus, Zap, ScrollText, Layers,
    UserCheck, ExternalLink, X, Award, BookOpen, Clock, QrCode, Loader2,
} from 'lucide-react';
import { getRyanHeroFontFamily } from '../constants/ryanHeroFonts';
import RyanCardPaymentBrick, { RyanCardFormData } from '../components/RyanCardPaymentBrick';
import GoogleLoginButton from '../../components/GoogleLoginButton';
import { useAuth } from '../../contexts/AuthContext';
import { makeRyanTracker, flushRyanAnalytics, getReferralCode } from '../utils/ryanAnalytics';
import RyanTestModeBadge from '../components/RyanTestModeBadge';
import './RyanProductPage.css';
import './RyanCheckoutPage.css';

// ── Tipos (subconjunto do payload de GET /produto/:slug — mesma rota já
//    usada por RyanProductPage.tsx, nada novo aqui) ────────────────────────

interface Recommendation {
    productId: string;
    title: string;
    basePriceCents: number;
    priceCents: number;
    description: string;
    highlights: string[];
    badge: string;
    publicSlug: string | null;
}

interface CheckoutProductData {
    _id: string;
    title: string;
    priceCents: number;
    publicPage: {
        pixKey: string;
        pixHolderName: string;
        pixQrCodeDataUrl: string | null;
        whatsappNumber: string;
        heroDescription: string;
        heroTitleFont?: string;
        checkoutShowFlashcards: boolean;
        checkoutShowApostilas: boolean;
        checkoutShowDecks: boolean;
    };
    content: {
        totals: { flashcards: number; apostilas: number; subdecks: number };
    } | null;
    recommendations: Recommendation[];
}

// Dados da cobrança PIX automática, retornados por POST /criar-cobranca —
// vem do Mercado Pago em tempo real, nunca mais dos campos manuais
// pixKey/pixQrCodeDataUrl configurados pelo admin (esses continuam existindo
// no backend só pro fluxo de comprovante manual, que deixou de ser exposto
// aqui por padrão).
interface PixChargeData {
    qrCode: string;
    qrCodeBase64: string | null;
    ticketUrl: string | null;
}

interface SuccessInfo {
    productTitle: string;
    accessToken?: string;
    refreshToken?: string;
}

type PaymentPhase = 'choose' | 'pix' | 'card' | 'confirming' | 'success' | 'failed';

// Traduz status_detail técnico do Mercado Pago pra uma frase que o aluno
// entende — cobre os casos mais comuns de recusa de cartão; qualquer coisa
// fora dessa lista cai no fallback genérico.
function readableCardStatus(statusDetail?: string | null): string | null {
    const messages: Record<string, string> = {
        cc_rejected_insufficient_amount: 'Saldo insuficiente no cartão.',
        cc_rejected_bad_filled_card_number: 'Número do cartão inválido.',
        cc_rejected_bad_filled_date: 'Data de validade inválida.',
        cc_rejected_bad_filled_security_code: 'CVV inválido.',
        cc_rejected_bad_filled_other: 'Algum dado do cartão está incorreto.',
        cc_rejected_call_for_authorize: 'Seu banco pediu autorização manual — ligue pra ele antes de tentar de novo.',
        cc_rejected_card_disabled: 'Cartão desabilitado para compras online. Fale com seu banco.',
        cc_rejected_duplicated_payment: 'Já identificamos uma tentativa igual a essa recentemente.',
        cc_rejected_high_risk: 'Pagamento recusado por segurança. Tente outro cartão ou o Pix.',
        cc_rejected_max_attempts: 'Número máximo de tentativas atingido. Tente outro cartão.',
        cc_rejected_other_reason: 'Cartão recusado pelo banco.',
        // Código técnico do Mercado Pago sem descrição pública oficial —
        // observado em teste manual associado a CVV inválido (titular de
        // teste SECU). Mapeado aqui por precaução, caso volte a aparecer
        // como status_detail de um pagamento 'rejected' — o fallback
        // genérico abaixo já cobre qualquer OUTRO código não mapeado.
        not_result_by_params: 'Não conseguimos validar os dados do cartão. Confira número, validade e CVV e tente novamente.',
    };
    // Fallback genérico pra qualquer código não mapeado (hoje ou no futuro,
    // se o Mercado Pago introduzir um novo) — nunca deixa um código técnico
    // vazar cru pra tela.
    return statusDetail ? (messages[statusDetail] || 'Não conseguimos processar seu cartão. Verifique os dados e tente novamente.') : null;
}

// ── Helpers reaproveitados tal qual de RyanProductPage.tsx (mesmas funções,
//    duplicadas aqui porque não são exportadas de lá — mesmo padrão já
//    tolerado em outros arquivos desta sessão, ex: buildWhatsappUrl) ───────

function formatPrice(cents: number): string {
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function buildWhatsappUrl(number: string, productTitle: string, message?: string): string {
    const cleaned = number.replace(/\D/g, '');
    const full = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
    const msg = encodeURIComponent(
        message ?? `Olá! Quero adquirir "${productTitle}" e estou enviando o comprovante do PIX.`
    );
    return `https://wa.me/${full}?text=${msg}`;
}

// getReferralCode agora vem de ryanAnalytics.ts (fonte única com o tracker,
// evita a leitura divergir entre comissão e analytics) — só a gravação
// continua aqui, ela nunca teve uma segunda cópia pra convergir.
function saveReferralCode(slug: string, code: string) {
    try {
        localStorage.setItem(`ryan_ref_${slug}`, JSON.stringify({ code, savedAt: Date.now() }));
    } catch { /* localStorage indisponível — segue sem rastrear */ }
}

// ── Persistência do progresso do checkout — sobrevive a um refresh no meio
// do processo (ex: pagamento com cartão fica pendente, a pessoa atualiza a
// página achando que travou). TTL de 4h: mais generoso que o timeout de
// polling (5min, cobre "esqueci a aba aberta um tempo"), mas sem reter
// indefinidamente um checkout abandonado de verdade.
const CHECKOUT_PROGRESS_TTL_MS = 4 * 60 * 60 * 1000;

interface CheckoutProgress {
    accessRequestId: string | null;
    email: string;
    identificationDone: boolean;
    selectedRecIds: string[];
    finalName: string;
    wasNewAccount: boolean;
    savedAt: number;
}

function saveCheckoutProgress(slug: string, progress: Omit<CheckoutProgress, 'savedAt'>) {
    try {
        localStorage.setItem(`ryan_checkout_progress_${slug}`, JSON.stringify({ ...progress, savedAt: Date.now() }));
    } catch { /* localStorage indisponível (ex: modo privado) — segue sem persistir */ }
}

function loadCheckoutProgress(slug: string): CheckoutProgress | null {
    try {
        const raw = localStorage.getItem(`ryan_checkout_progress_${slug}`);
        if (!raw) return null;
        const parsed: CheckoutProgress = JSON.parse(raw);
        if (!parsed.savedAt || Date.now() - parsed.savedAt > CHECKOUT_PROGRESS_TTL_MS) {
            localStorage.removeItem(`ryan_checkout_progress_${slug}`);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function clearCheckoutProgress(slug: string) {
    try { localStorage.removeItem(`ryan_checkout_progress_${slug}`); } catch { /* ignora */ }
}

// Interpola um número até o valor-alvo em vez de trocar instantaneamente —
// é o "contador do total aumentando" que dá vida ao resumo quando uma
// recomendação é adicionada/removida. Puramente de apresentação (não afeta
// o valor real usado nas chamadas de rede, só o que é mostrado na tela).
function useAnimatedNumber(target: number, duration = 380): number {
    const [display, setDisplay] = useState(target);
    const prevRef = useRef(target);
    useEffect(() => {
        const from = prevRef.current;
        const to = target;
        if (from === to) return;
        let start: number | null = null;
        let raf = 0;
        const step = (ts: number) => {
            if (start === null) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(from + (to - from) * eased));
            if (progress < 1) raf = requestAnimationFrame(step);
            else prevRef.current = to;
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return display;
}

const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

// Conteúdo canônico dos 3 passos da jornada inteira (não só pós-pagamento) —
// existe só em DOIS lugares (a versão completa, visível apenas no mobile — no
// desktop a sidebar já mostra a versão compacta, então repetir a lista
// inteira ali seria redundante) e nunca com redação diferente entre eles.
// Reflete o pagamento automático (Pix/cartão via Mercado Pago) — o fluxo de
// comprovante manual deixou de ser o caminho apresentado por padrão aqui.
const HOW_IT_WORKS_STEPS = ['Escolha Pix ou cartão', 'Pague com segurança', 'Acesso liberado automaticamente'];

const STEPS: { key: 'produto' | 'ident' | 'pagamento'; label: string }[] = [
    { key: 'produto', label: 'Produto' },
    { key: 'ident', label: 'Identificação' },
    { key: 'pagamento', label: 'Pagamento' },
];

type IdentMode = 'signup' | 'login';
type StepState = 'done' | 'current' | 'upcoming';
type StepKey = typeof STEPS[number]['key'];

// Único id de "elemento compartilhado" do passo a passo — é o que faz o
// Framer Motion entender que a cópia que está sumindo de um lugar e a que
// está aparecendo em outro são "a mesma coisa" e deve animar uma transição
// de posição/tamanho entre as duas (FLIP), em vez de um fade. Só pode haver
// UMA instância desse layoutId montada por vez (por isso o passo a passo é
// renderizado condicionalmente no header OU na sidebar, nunca nos dois).
const STEPS_LAYOUT_ID = 'rcp-steps-shared';
// Sem bounce/overshoot — só um ease-in-out suave — conforme pedido.
const STEPS_TRANSITION = { duration: 0.55, ease: [0.65, 0, 0.35, 1] as [number, number, number, number] };

// Indicador de progresso reaproveitado em DOIS lugares (header + resumo na
// sidebar) — mesmo componente, pra garantir que as duas aparições sejam
// visualmente idênticas (é o que faz a transição entre elas parecer uma
// continuação, não duas coisas diferentes).
const StepsIndicator: React.FC<{ getState: (key: StepKey) => StepState }> = ({ getState }) => (
    <div className="rcp-steps">
        {STEPS.map((s, i) => {
            const state = getState(s.key);
            return (
                <React.Fragment key={s.key}>
                    <div className={`rcp-step rcp-step--${state}`}>
                        <span className="rcp-step-dot">{state === 'done' ? <Check size={12} /> : i + 1}</span>
                        <span className="rcp-step-label">{s.label}</span>
                    </div>
                    {i < STEPS.length - 1 && <span className="rcp-step-connector" />}
                </React.Fragment>
            );
        })}
    </div>
);

// ══════════════════════════════════════════════════════════════════════
// PÁGINA
// ══════════════════════════════════════════════════════════════════════

const RyanCheckoutPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Referral code que já estava salvo pra este slug ANTES desta visita —
    // capturado no lazy initializer do useState (roda durante o primeiro
    // render, antes de qualquer efeito) porque o efeito que grava o `rep` da
    // URL atual (mais abaixo) roda ANTES do efeito de restauração de
    // progresso (efeitos rodam na ordem em que são declarados) e sobrescreve
    // esse valor — se eu lesse depois, nunca veria o código "antigo" pra
    // comparar.
    const [prevReferralCode] = useState<string | null>(() => (slug ? getReferralCode(slug) : null));
    // AuthContext principal (contexts/AuthContext) — o mesmo usado pelas páginas
    // compartilhadas (/study, /view, /editor, /resumo) e pelo header/Área do
    // Aluno do Ryan. É montado UMA VEZ, fora do <Router>, em App.tsx — não
    // remonta ao navegar daqui pra /ryan. Precisamos de checkAuth() explicitamente
    // em enterPlatform (ver comentário lá) pra sincronizar esse contexto
    // já montado com o token recém-gravado no autologin pós-pagamento.
    const { checkAuth, user, isAuthenticated } = useAuth();

    const [product, setProduct] = useState<CheckoutProductData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Recomendações: cada uma é um toggle local independente, sem chamada de
    // rede — todo o necessário (título, preço, desconto) já veio no payload
    // inicial do GET /produto/:slug. A rede só entra quando a identificação é
    // concluída (completeIdentification), que decide sozinha se chama
    // /solicitar-acesso (nenhuma marcada) ou /aceitar-recomendacoes (1+).
    const [selectedRecIds, setSelectedRecIds] = useState<Set<string>>(new Set());

    // Cupom de desconto — prévia só-leitura (POST /validar-cupom), sem
    // reservar vaga nenhuma; a reserva de verdade acontece só quando
    // completeIdentification confirma o pedido (mesmo ponto que trava as
    // recomendações). appliedCoupon fica null até uma prévia bem-sucedida.
    const [couponInput, setCouponInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discountCents: number } | null>(null);
    const [couponError, setCouponError] = useState<string | null>(null);
    const [couponChecking, setCouponChecking] = useState(false);
    // Avisa quando a reserva do cupom foi perdida entre uma tentativa de
    // pagamento e outra (ver reacquireCouponIfReleased no backend) — a
    // compra segue sem o desconto, nunca é bloqueada por isso.
    const [couponLostNotice, setCouponLostNotice] = useState(false);

    // Identificação — duas abas explícitas ("Ainda não tenho conta" /
    // "Já tenho conta"), sem mais decisão automática por trás de um único
    // campo de email (ver Parte 3 do prompt que substituiu o fluxo antigo).
    const [identMode, setIdentMode] = useState<IdentMode>('signup');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [identificationDone, setIdentificationDone] = useState(false);
    const [finalName, setFinalName] = useState('');
    const [finalEmail, setFinalEmail] = useState('');
    // Diferencia a mensagem de confirmação: só diz "sua conta foi criada"
    // quando uma conta nova de fato nasceu aqui (aba "Ainda não tenho
    // conta") — pra quem só fez login (conta já existia), isso seria falso.
    const [wasNewAccount, setWasNewAccount] = useState(false);
    // true quando, na aba "Ainda não tenho conta", detectamos (no blur do
    // email ou no submit) que aquele email já tem conta — em vez do beco sem
    // saída de antes (erro só no submit, "atualize a página"), oferece
    // trocar direto pra aba de login com o email já preenchido.
    const [duplicateEmailNotice, setDuplicateEmailNotice] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);
    // Termos de Uso — exigido só na aba "Ainda não tenho conta" (manual ou via
    // Google), nunca na aba "Já tenho conta" (a conta já existe de antes).
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    // Quem chega já logado (ex.: veio do botão "Quero adquirir" de dentro da
    // conta) se identifica com 1 clique, sem digitar senha — mesmo
    // completeIdentification(existingAccount:true) do login manual. "Usar outra
    // conta" volta pras abas de sempre.
    const [useOtherAccount, setUseOtherAccount] = useState(false);
    const sessionAccount = isAuthenticated && user?.email && !useOtherAccount ? user : null;
    const [googleAuthLoading, setGoogleAuthLoading] = useState(false);

    // Restauração de progresso salvo (Parte 2) — `restoring` mantém a tela de
    // carregamento até sabermos se há algo pra retomar, evitando um flash do
    // formulário vazio antes do estado restaurado aparecer. `restoredNotice`
    // mostra o aviso "continuando de onde parou" + link pra recomeçar.
    const [restoring, setRestoring] = useState(true);
    const [restoredNotice, setRestoredNotice] = useState(false);

    // Pagamento automático — accessRequestId é o elo entre a RyanAccessRequest
    // já criada (completeIdentification) e as chamadas de criar-cobranca /
    // checkout/status que vêm depois.
    const [accessRequestId, setAccessRequestId] = useState<string | null>(null);
    const [paymentPhase, setPaymentPhase] = useState<PaymentPhase>('choose');
    const [chargeCreating, setChargeCreating] = useState(false);
    const [chargeError, setChargeError] = useState<string | null>(null);
    const [pixCharge, setPixCharge] = useState<PixChargeData | null>(null);
    // Força remontagem TOTAL do RyanCardPaymentBrick a cada entrada na etapa
    // de cartão (troca de `key` = React destrói a instância antiga e cria
    // uma nova do zero) — mesmo o Brick já tratando isso internamente via
    // container id único, esta é uma segunda camada de garantia contra o
    // botão de pagar ficar travado depois de uma recusa (Problema 1: sem
    // isso, tentar de novo sem dar refresh na página inteira não reabilitava
    // o formulário).
    const [cardBrickKey, setCardBrickKey] = useState(0);
    const [pollingTimedOut, setPollingTimedOut] = useState(false);
    const [checkingNow, setCheckingNow] = useState(false);
    const [failureDetail, setFailureDetail] = useState<string | null>(null);
    const [successInfo, setSuccessInfo] = useState<SuccessInfo | null>(null);
    const pollIntervalRef = useRef<number | null>(null);
    const pollStartRef = useRef<number>(0);
    // Guarda a versão mais recente de checkStatusOnce (definida mais abaixo,
    // depois do return antecipado de loading/erro) — o efeito de
    // visibilitychange/focus precisa ficar ANTES desse return pra não violar
    // a regra dos hooks (nunca condicionais), mas só pode chamar a função de
    // fato através de uma ref, já que ela ainda não existe nesse ponto do
    // corpo do componente.
    const checkStatusOnceRef = useRef<((requestId: string) => Promise<boolean>) | null>(null);
    // Espelha paymentPhase num ref pro listener de pagehide/visibilitychange
    // (registrado uma única vez, ver efeito abaixo) ler a fase ATUAL sem
    // precisar recriar o listener a cada transição de fase.
    const paymentPhaseRef = useRef<PaymentPhase>('choose');
    useEffect(() => { paymentPhaseRef.current = paymentPhase; }, [paymentPhase]);

    const [copiedPix, setCopiedPix] = useState(false);
    const [justAddedId, setJustAddedId] = useState<string | null>(null);
    // Pulso breve no card de resumo inteiro (não só no número) sempre que o
    // pedido muda — reforça "meu pedido mudou" mesmo sem atenção total.
    const [summaryPulse, setSummaryPulse] = useState(false);
    // Controla se o passo a passo "mora" no header (hero visível, uso normal)
    // ou "se mudou" pro topo do resumo na sidebar (hero já saiu da tela ao
    // rolar) — puramente de apresentação, observando a posição de um marcador
    // no fim do hero, sem nenhuma lógica de negócio nova.
    const [heroVisible, setHeroVisible] = useState(true);

    const identRevealRef = useRef<HTMLDivElement>(null);
    const paymentRef = useRef<HTMLDivElement>(null);
    const heroSentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => { if (slug) fetchProduct(slug); }, [slug]);

    useEffect(() => {
        const el = heroSentinelRef.current;
        // O sentinel só existe no DOM depois que `product` carrega (antes
        // disso a página está no branch de loading, sem esse JSX) — precisa
        // depender de `product` pra tentar de novo assim que ele nascer, senão
        // esse efeito roda uma única vez, cedo demais, acha `el` nulo e nunca
        // mais cria o observer (heroVisible fica travado em `true` pra sempre).
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setHeroVisible(entry.isIntersecting),
            // rootMargin negativo no topo antecipa o gatilho em ~60px ao
            // descer a página — sem isso, a troca só acontecia exatamente
            // quando o sentinel cruzava o topo real da viewport, o que dava
            // uma sensação de leve atraso.
            { threshold: 0, rootMargin: '-60px 0px 0px 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [product]);

    useEffect(() => {
        const rep = searchParams.get('rep');
        if (slug && rep) saveReferralCode(slug, rep);
    }, [slug, searchParams]);

    useEffect(() => {
        document.title = product ? `Checkout — ${product.title} | Ryan` : 'Checkout | Ryan';
        return () => { document.title = 'Ryan'; };
    }, [product]);

    useEffect(() => {
        if (duplicateEmailNotice) {
            identRevealRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [duplicateEmailNotice]);

    // Força uma checagem imediata ao voltar pra aba — Pix exige sair do
    // navegador (abrir o app do banco), e mobile costuma suspender/throttlar
    // setInterval de aba em segundo plano. Sem isso, a pessoa só via o
    // resultado no próximo tick "de verdade" do timer, que podia já ter
    // estourado o timeout de 5min de tanto ter ficado suspenso, mesmo com o
    // pagamento aprovado há minutos. Roda mesmo depois do timeout (inclusive
    // quando pollingTimedOut já é true) — voltar à aba é justamente o
    // momento certo de tentar de novo, não só o link manual.
    useEffect(() => {
        if (!accessRequestId) return;
        if (paymentPhase !== 'pix' && paymentPhase !== 'confirming') return;
        const forceCheck = () => {
            if (document.visibilityState !== 'visible') return;
            checkStatusOnceRef.current?.(accessRequestId);
        };
        document.addEventListener('visibilitychange', forceCheck);
        window.addEventListener('focus', forceCheck);
        return () => {
            document.removeEventListener('visibilitychange', forceCheck);
            window.removeEventListener('focus', forceCheck);
        };
    }, [accessRequestId, paymentPhase]);

    useEffect(() => {
        if (identificationDone) {
            paymentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [identificationDone]);

    // Garante que o polling nunca continue rodando depois que a pessoa sai
    // desta página (ex: navega pra outra rota antes do pagamento confirmar).
    useEffect(() => () => {
        if (pollIntervalRef.current) window.clearInterval(pollIntervalRef.current);
    }, []);

    // checkout_abandoned — registrado uma única vez, assim que product/slug
    // existem. Dispara em pagehide (fechar aba/navegar pra fora) e em
    // visibilitychange:hidden (trocar de app no mobile sem fechar a aba), só
    // quando o pagamento ainda não foi confirmado. flushRyanAnalytics() logo
    // em seguida garante que ESTE evento saia via sendBeacon já nesta mesma
    // chamada, sem depender de outro listener rodar depois pra pegá-lo.
    useEffect(() => {
        if (!product || !slug) return;
        const tracker = makeRyanTracker(product._id, slug, 'checkout');
        const handleLeave = () => {
            if (paymentPhaseRef.current !== 'success') {
                tracker.track('checkout_abandoned', { lastStep: paymentPhaseRef.current });
                flushRyanAnalytics();
            }
        };
        window.addEventListener('pagehide', handleLeave);
        const handleVisibility = () => { if (document.visibilityState === 'hidden') handleLeave(); };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.removeEventListener('pagehide', handleLeave);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [product, slug]);

    // Restaura progresso salvo (Parte 2) — roda uma vez, assim que `slug`
    // está disponível. Se havia um accessRequestId salvo, consulta o status
    // real (GET /checkout/status) pra saber onde retomar: aprovado → sucesso
    // direto; ainda aguardando (o cenário reproduzido — cartão CONT, página
    // atualizada no meio do processo) → volta a pollar o MESMO pagamento,
    // sem recriar cobrança nenhuma. Qualquer outro status (falhou, cancelado,
    // desconhecido) é tratado como tentativa MORTA — não vale a pena retomar
    // do mesmo jeito que um pagamento genuinamente pendente, então não trava
    // o usuário na tela de identificação escondida/pagamento fantasma; só
    // mantém os campos leves já restaurados (email/nome/recomendações) e
    // descarta o progresso salvo.
    useEffect(() => {
        if (!slug) { setRestoring(false); return; }
        const saved = loadCheckoutProgress(slug);
        if (!saved) { setRestoring(false); return; }

        // Link de embaixador diferente do que gerou esse progresso salvo é
        // sinal forte de que pode ser OUTRA pessoa no mesmo dispositivo (ex:
        // link compartilhado, aparelho emprestado) — por padrão, não
        // restaura nada silenciosamente nesse caso, descarta o progresso
        // antigo e começa do zero (Investigação item 4).
        const currentRep = searchParams.get('rep');
        if (currentRep && prevReferralCode && currentRep !== prevReferralCode) {
            clearCheckoutProgress(slug);
            setRestoring(false);
            return;
        }

        setEmail(saved.email || '');
        setSelectedRecIds(new Set(saved.selectedRecIds || []));
        setFinalName(saved.finalName || '');
        setFinalEmail(saved.email || '');
        setWasNewAccount(!!saved.wasNewAccount);
        // SEMPRE visível e transparente assim que QUALQUER dado é restaurado
        // — nunca preenche o formulário calado. Antes, isso só era setado lá
        // embaixo, dentro do bloco assíncrono (só quando havia accessRequestId
        // salvo); um progresso "leve" (só email digitado, identificação nunca
        // concluída — o cenário relatado: formulário de identificação
        // reaparecendo pré-preenchido) preenchia o campo sem avisar nada e
        // sem nenhuma saída óbvia além do DevTools.
        setRestoredNotice(true);

        if (!saved.accessRequestId) {
            setRestoring(false);
            return;
        }

        (async () => {
            try {
                const res = await fetch(`/api/ryan/checkout/status/${saved.accessRequestId}`);
                if (!res.ok) { clearCheckoutProgress(slug); return; }
                const data: { status: string; productTitle: string; accessToken?: string; refreshToken?: string } = await res.json();

                if (data.status === 'approved') {
                    setAccessRequestId(saved.accessRequestId);
                    setIdentificationDone(true);
                    setSuccessInfo({ productTitle: data.productTitle, accessToken: data.accessToken, refreshToken: data.refreshToken });
                    setPaymentPhase('success');
                    clearCheckoutProgress(slug);
                } else if (data.status === 'awaiting_payment') {
                    // Pagamento genuinamente em andamento (cobrança já criada no
                    // gateway, aguardando confirmação do webhook) — vale a pena
                    // continuar pollando o MESMO pagamento em vez de recomeçar.
                    setAccessRequestId(saved.accessRequestId);
                    setIdentificationDone(true);
                    setPaymentPhase('confirming');
                    startPolling(saved.accessRequestId as string);
                } else if (data.status === 'pending') {
                    // Identificação concluída, mas nenhuma cobrança chegou a ser
                    // criada ainda (nunca escolheu Pix/cartão) — resumo benigno,
                    // não é uma tentativa falha/abandonada, só retoma de onde
                    // parou na escolha do método.
                    setAccessRequestId(saved.accessRequestId);
                    setIdentificationDone(true);
                    setPaymentPhase('choose');
                } else {
                    // payment_failed ou rejected — tentativa MORTA (Investigação
                    // item 1: não deve se comportar como uma retomada genuína).
                    // Descarta o progresso salvo (não faz sentido consultar de
                    // novo no próximo carregamento) e deixa o usuário no fluxo
                    // normal (paymentPhase permanece 'choose', identificationDone
                    // permanece false), com o email já preenchido e o aviso de
                    // "Não é você? Recomeçar" visível — nunca escondido atrás de
                    // uma tela de pagamento fantasma.
                    clearCheckoutProgress(slug);
                }
            } catch {
                clearCheckoutProgress(slug);
            } finally {
                setRestoring(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Persiste o progresso a cada mudança relevante — só depois que a
    // restauração inicial já rodou (senão sobrescreveria o que acabou de ser
    // lido com um estado ainda em branco) e só quando já existe algo que vale
    // a pena reter (email digitado ou solicitação já criada).
    useEffect(() => {
        if (restoring || !slug) return;
        if (!email.trim() && !accessRequestId) return;
        saveCheckoutProgress(slug, {
            accessRequestId,
            email,
            identificationDone,
            selectedRecIds: Array.from(selectedRecIds),
            finalName,
            wasNewAccount,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, restoring, accessRequestId, email, identificationDone, selectedRecIds, finalName, wasNewAccount]);

    const fetchProduct = async (s: string) => {
        try {
            const res = await fetch(`/api/ryan/produto/${s}`);
            if (!res.ok) throw new Error('not_found');
            const data = await res.json();
            setProduct(data.product);
            makeRyanTracker(data.product._id, s, 'checkout').track('checkout_started');
        } catch {
            setError('Produto não encontrado.');
        } finally {
            setLoading(false);
        }
    };

    // Derivados de `product` calculados com fallback seguro e ANTES dos
    // retornos antecipados de loading/erro abaixo — useAnimatedNumber chama
    // hooks internamente, e hooks nunca podem vir depois de um return
    // condicional (regra do React), então precisa ser chamado incondicionalmente
    // aqui em cima, mesmo enquanto `product` ainda é null.
    const recommendations = product?.recommendations || [];
    const selectedRecs = recommendations.filter(r => selectedRecIds.has(r.productId));
    const recsSum = selectedRecs.reduce((sum, r) => sum + r.priceCents, 0);
    const recsBaseSum = selectedRecs.reduce((sum, r) => sum + r.basePriceCents, 0);
    const preCouponTotal = (product?.priceCents || 0) + recsSum;
    const couponDiscountCents = appliedCoupon?.discountCents || 0;
    const runningTotal = Math.max(0, preCouponTotal - couponDiscountCents);
    const runningSavings = recsBaseSum - recsSum;
    const animatedTotal = useAnimatedNumber(runningTotal);

    if (loading || restoring) {
        return (
            <div className="rpp-loading">
                <div className="rpp-spinner" />
                <span>Carregando...</span>
            </div>
        );
    }

    if (error || !product || !slug) {
        return (
            <div className="rpp-error">
                <div className="rpp-error-icon"><AlertCircle size={28} /></div>
                <h2>Produto indisponível</h2>
                <p>Este link não existe ou está temporariamente desativado.</p>
                <a href="/ryan" className="rpp-error-link">Voltar ao Ryan →</a>
            </div>
        );
    }

    const totals = product.content?.totals;
    const statCards = [
        product.publicPage.checkoutShowFlashcards && totals ? { icon: Zap, value: totals.flashcards, label: 'Flashcards' } : null,
        product.publicPage.checkoutShowApostilas && totals ? { icon: ScrollText, value: totals.apostilas, label: 'Apostilas' } : null,
        product.publicPage.checkoutShowDecks && totals ? { icon: Layers, value: totals.subdecks, label: 'Decks' } : null,
    ].filter((c): c is { icon: typeof Zap; value: number; label: string } => c !== null);

    // Indicador de progresso — "Produto" vira done assim que a pessoa começa
    // a se identificar (proxy: já digitou algo no email, comum às duas abas);
    // "Pagamento" só vira done quando o polling confirma approved de verdade
    // (paymentPhase === 'success'), nunca só por existir uma cobrança criada
    // — reflete que só o webhook concede acesso.
    const stepState = (key: typeof STEPS[number]['key']): StepState => {
        const identStarted = identificationDone || email.trim().length > 0;
        if (key === 'produto') return identStarted ? 'done' : 'current';
        if (key === 'ident') {
            if (identificationDone) return 'done';
            return identStarted ? 'current' : 'upcoming';
        }
        if (paymentPhase === 'success') return 'done';
        return identificationDone ? 'current' : 'upcoming';
    };

    const pulseSummary = () => {
        setSummaryPulse(true);
        setTimeout(() => setSummaryPulse(false), 650);
    };

    const toggleRec = (id: string) => {
        if (identificationDone) return; // solicitação já criada — trava a escolha
        setSelectedRecIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
                setJustAddedId(id);
                setTimeout(() => setJustAddedId(cur => (cur === id ? null : cur)), 700);
            }
            return next;
        });
        pulseSummary();
    };

    // Checagem pontual de "esse email já tem conta" — reaproveitada tanto no
    // blur do campo (aba "Ainda não tenho conta") quanto no submit (rede de
    // segurança se a pessoa colar/editar o email depois do blur, sem sair do
    // campo de novo). Mesma rota de sempre (POST /verificar-conta), agora
    // corrigida no backend pra achar contas criadas mid-checkout mesmo sem
    // pagamento confirmado ainda (era a causa raiz do bug reproduzido).
    const checkEmailExists = async (em: string): Promise<boolean> => {
        if (!slug) return false;
        try {
            const res = await fetch(`/api/ryan/produto/${slug}/verificar-conta`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: em }),
            });
            if (!res.ok) return false;
            const data: { found: boolean } = await res.json();
            return !!data.found;
        } catch {
            return false;
        }
    };

    const handleSignupEmailBlur = async () => {
        if (identMode !== 'signup' || !email.trim()) return;
        setCheckingEmail(true);
        const exists = await checkEmailExists(email.trim());
        setCheckingEmail(false);
        setDuplicateEmailNotice(exists);
    };

    const switchToLogin = () => {
        setIdentMode('login');
        setDuplicateEmailNotice(false);
        setSubmitError(null);
        setPassword('');
    };

    // Prévia só-leitura — não reserva vaga nenhuma (isso só acontece em
    // completeIdentification, no submit real). Reenvia recommendedProductIds
    // pra calcular o desconto sobre o mesmo total que o pedido final vai usar
    // (produto + recomendações já escolhidas nesse momento).
    const applyCoupon = async () => {
        const code = couponInput.trim();
        if (!code || !slug) return;
        setCouponChecking(true);
        setCouponError(null);
        try {
            const res = await fetch(`/api/ryan/produto/${slug}/validar-cupom`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, recommendedProductIds: Array.from(selectedRecIds) }),
            });
            const data = await res.json().catch(() => ({} as { message?: string; code?: string; discountCents?: number }));
            if (!res.ok) {
                setCouponError(data.message || 'Cupom inválido.');
                return;
            }
            setAppliedCoupon({ code: data.code!, discountCents: data.discountCents || 0 });
            setCouponInput('');
        } catch {
            setCouponError('Erro de conexão. Tente novamente.');
        } finally {
            setCouponChecking(false);
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponError(null);
    };

    // Cria a RyanAccessRequest — via /aceitar-recomendacoes se 1+ produtos
    // recomendados foram adicionados, ou /solicitar-acesso caso contrário.
    // Mesmas rotas já usadas antes, só chamadas no fim da identificação (a
    // escolha de recomendações já aconteceu antes, lá em cima). `pw` só é
    // enviado no caminho "conta não encontrada" — o backend cria o User ali
    // (mesma validação de senha da Parte 1: mínimo 6 caracteres). As duas
    // rotas devolvem o mesmo requestId que o resto do fluxo de pagamento
    // precisa — só o formato do payload de entrada diverge (recomendações ou
    // não), por isso um único endpoint calculado em vez de duas cópias.
    const completeIdentification = async (n: string, em: string, wa: string, existingAccount: boolean, pw?: string) => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            const hasRecs = selectedRecIds.size > 0;
            const endpoint = hasRecs ? 'aceitar-recomendacoes' : 'solicitar-acesso';
            const res = await fetch(`/api/ryan/produto/${slug}/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: n, email: em, whatsapp: wa,
                    referralCode: getReferralCode(slug), existingAccount,
                    ...(hasRecs ? { recommendedProductIds: Array.from(selectedRecIds) } : {}),
                    ...(pw ? { password: pw, confirmPassword: pw } : {}),
                    ...(appliedCoupon ? { couponCode: appliedCoupon.code } : {}),
                }),
            });
            const data = await res.json().catch(() => ({} as { message?: string; requestId?: string }));
            if (!res.ok) {
                // Cupom pode ter esgotado entre a prévia e este submit (outra
                // pessoa levou a última vaga) — mensagem específica pra isso,
                // já que a solução é diferente de um erro genérico: remover
                // o cupom e tentar de novo, não só "tente novamente".
                if (appliedCoupon && res.status === 409 && /cupom/i.test(data.message || '')) {
                    setCouponError(data.message || 'Este cupom esgotou. Remova-o para continuar.');
                    setSubmitError('O cupom aplicado não está mais disponível — remova-o no resumo do pedido pra continuar.');
                    return;
                }
                throw new Error(data.message || 'Erro ao enviar solicitação.');
            }

            setAccessRequestId(data.requestId || null);
            setFinalName(n);
            setFinalEmail(em);
            setWasNewAccount(!existingAccount);
            setIdentificationDone(true);
            setPaymentPhase('choose');
            makeRyanTracker(product._id, slug, 'checkout').track('checkout_step', { step: 'identification_submitted', wasNewAccount: !existingAccount });
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Erro de conexão. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Pagamento automático (Pix/cartão via Mercado Pago) ──────────────────

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const POLL_INTERVAL_MS = 3000;
    const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutos

    // Extraído de handleEnterPlatform (botão manual) pra ser reaproveitado
    // pelo redirect automático em checkStatusOnce — os tokens são passados
    // como parâmetro (vindos direto da resposta do fetch), nunca lidos de
    // `successInfo` logo depois de um setSuccessInfo(...) na mesma função,
    // porque o setState é assíncrono e o valor ainda não teria atualizado
    // no momento da leitura (bug sutil que quase reintroduzimos aqui).
    const enterPlatform = async (accessToken?: string, refreshToken?: string) => {
        if (accessToken) {
            localStorage.setItem('accessToken', accessToken);
            if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
            // Ver comentário longo em handleEnterPlatform sobre por que esse
            // await é obrigatório antes de navegar (bug de header/Área do
            // Aluno vazios sem ele).
            await checkAuth();
        }
        navigate('/ryan');
    };

    // Uma consulta de status — devolve true quando o polling deve parar
    // (resolveu pra approved ou payment_failed). Erro de rede pontual não
    // para o polling, só tenta de novo na próxima batida.
    const checkStatusOnce = async (requestId: string): Promise<boolean> => {
        try {
            const res = await fetch(`/api/ryan/checkout/status/${requestId}`);
            if (!res.ok) return false;
            const data: { status: string; productTitle: string; accessToken?: string; refreshToken?: string } = await res.json();
            if (data.status === 'approved') {
                setSuccessInfo({ productTitle: data.productTitle, accessToken: data.accessToken, refreshToken: data.refreshToken });
                setPaymentPhase('success');
                if (slug) clearCheckoutProgress(slug);
                makeRyanTracker(product._id, slug, 'checkout').track('payment_approved');
                // Redirect automático — não exige mais o clique manual em
                // "Entrar na plataforma". Ver incidente real: pagamento Pix
                // aprovado, tela ficou presa em "confirmando" mesmo com o
                // acesso já correto no backend (causa real era o polling
                // nunca detectar e o timeout de 5min sem retomada — ver
                // listener de visibilitychange abaixo —, mas exigir um
                // clique a mais depois de detectar também não ajudava).
                await enterPlatform(data.accessToken, data.refreshToken);
                return true;
            }
            if (data.status === 'payment_failed') {
                setFailureDetail('Não conseguimos confirmar esse pagamento.');
                setPaymentPhase('failed');
                makeRyanTracker(product._id, slug, 'checkout').track('checkout_step', { step: 'payment_failed' });
                return true;
            }
            return false;
        } catch {
            return false;
        }
    };

    const startPolling = (requestId: string) => {
        stopPolling();
        setPollingTimedOut(false);
        pollStartRef.current = Date.now();
        pollIntervalRef.current = window.setInterval(async () => {
            if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
                stopPolling();
                setPollingTimedOut(true);
                return;
            }
            const resolved = await checkStatusOnce(requestId);
            if (resolved) stopPolling();
        }, POLL_INTERVAL_MS);
    };

    // Mantém a ref sempre apontando pra versão mais recente (fecha sobre
    // accessRequestId/slug/product atuais) — é o que o efeito de
    // visibilitychange/focus, declarado antes do return antecipado lá em
    // cima, chama de fato.
    checkStatusOnceRef.current = checkStatusOnce;

    const handleManualCheck = async () => {
        if (!accessRequestId) return;
        setCheckingNow(true);
        try {
            const resolved = await checkStatusOnce(accessRequestId);
            if (resolved) setPollingTimedOut(false);
        } finally {
            setCheckingNow(false);
        }
    };

    const createPixCharge = async () => {
        if (!accessRequestId) return;
        setChargeCreating(true);
        setChargeError(null);
        try {
            const res = await fetch(`/api/ryan/produto/${slug}/criar-cobranca`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessRequestId, paymentMethod: 'pix' }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Erro ao gerar o Pix.');
            // Cupom vinculado a esta request mas cuja reserva foi liberada
            // (tentativa anterior falhou) pode não ter conseguido prender a
            // vaga de novo — a compra segue, só sem o desconto; avisa quem
            // está comprando em vez de deixar o valor mudar silenciosamente.
            if (data.couponLost) setCouponLostNotice(true);
            setPixCharge({
                qrCode: data.pix?.qrCode || '',
                qrCodeBase64: data.pix?.qrCodeBase64 || null,
                ticketUrl: data.pix?.ticketUrl || null,
            });
            startPolling(accessRequestId);
        } catch (err) {
            setChargeError(err instanceof Error ? err.message : 'Erro de conexão. Tente novamente.');
        } finally {
            setChargeCreating(false);
        }
    };

    const choosePix = () => {
        setPaymentPhase('pix');
        createPixCharge();
        makeRyanTracker(product._id, slug, 'checkout').track('checkout_step', { step: 'payment_method_selected', method: 'pix' });
    };

    const chooseCard = () => {
        setChargeError(null);
        setCardBrickKey(k => k + 1);
        setPaymentPhase('card');
        makeRyanTracker(product._id, slug, 'checkout').track('checkout_step', { step: 'payment_method_selected', method: 'card' });
    };

    // "Trocar forma de pagamento" — só volta pra escolha visualmente; se já
    // existe uma cobrança em andamento (awaiting_payment), o backend recusa
    // criar uma segunda (evita cobrar duas vezes o mesmo pedido). Funciona
    // sempre depois de uma falha (payment_failed), que é o caso previsto de
    // "Tentar novamente".
    const backToChoice = () => {
        stopPolling();
        setPaymentPhase('choose');
        setChargeError(null);
        setPollingTimedOut(false);
    };

    const handleRetryPayment = () => {
        stopPolling();
        setPixCharge(null);
        setChargeError(null);
        setFailureDetail(null);
        setPollingTimedOut(false);
        setPaymentPhase('choose');
    };

    // onSubmit do RyanCardPaymentBrick — nunca recebe número/CVV crus, só o
    // token já gerado pelo próprio Brick. SEMPRE resolve (nunca rejeita) —
    // antes, uma falha de rede no fetch deixava a promise rejeitar pro Brick,
    // que assumia sozinho a UI de erro/retry dele; em teste manual no mobile
    // isso resultou no botão de pagar ficando travado sem dar refresh
    // (Problema 1). Agora TODO erro (rede, backend, cartão recusado) é
    // tratado aqui, com nosso próprio estado — e força uma remontagem
    // completa do Brick (cardBrickKey) pra nunca depender do estado interno
    // dele se recuperar sozinho de um erro anterior.
    const handleCardSubmit = async (formData: RyanCardFormData): Promise<void> => {
        if (!accessRequestId) return;
        setChargeError(null);
        try {
            const res = await fetch(`/api/ryan/produto/${slug}/criar-cobranca`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    accessRequestId,
                    paymentMethod: 'credit_card',
                    cardToken: formData.token,
                    installments: formData.installments,
                    issuerId: formData.issuer_id,
                    identification: formData.payer?.identification,
                    // Gerado pelo security.js (ver public/index.html) e enviado como
                    // X-meli-session-id pro Mercado Pago (ver ryanMercadoPagoService.js)
                    // — sem isso, o cartão chega "anônimo" pro antifraude deles, que é
                    // exatamente o cenário de maior chance de recusa por "controles de
                    // segurança" numa conta nova, sem histórico de transações ainda.
                    // Best-effort: se o script não terminou de gerar a tempo, undefined
                    // é omitido pelo JSON.stringify e o pagamento segue sem o header.
                    deviceId: (window as any).MP_DEVICE_SESSION_ID,
                }),
            });
            const data = await res.json().catch(() => ({} as { message?: string; status?: string; card?: { statusDetail?: string }; couponLost?: boolean }));
            if (!res.ok) {
                console.error('[handleCardSubmit] criar-cobranca respondeu erro:', { httpStatus: res.status, data });
                setChargeError(data.message || 'Erro ao processar pagamento.');
                setCardBrickKey(k => k + 1);
                return;
            }
            if (['rejected', 'cancelled'].includes(data.status || '')) {
                console.log('[handleCardSubmit] pagamento recusado pelo gateway:', data);
                setFailureDetail(readableCardStatus(data.card?.statusDetail) || 'Cartão recusado pelo banco.');
                setPaymentPhase('failed');
                makeRyanTracker(product._id, slug, 'checkout').track('checkout_step', { step: 'payment_failed' });
                return;
            }
            // Mesmo aviso de cupom perdido do fluxo Pix (ver createPixCharge).
            if (data.couponLost) setCouponLostNotice(true);
            // Aprovado na hora ou ainda processando — nos dois casos só o webhook
            // concede acesso de fato (mesma regra do criar-cobranca no backend),
            // então sempre confirma via polling antes de considerar concluído.
            setPaymentPhase('confirming');
            startPolling(accessRequestId);
        } catch (err) {
            console.error('[handleCardSubmit] erro de rede ao chamar criar-cobranca:', err);
            setChargeError('Erro de conexão. Verifique sua internet e tente novamente.');
            setCardBrickKey(k => k + 1);
        }
    };

    // Botão manual (link "Verificar agora"/fallback pós-timeout) — mesma
    // lógica do redirect automático de checkStatusOnce, só que lendo de
    // successInfo (aqui sim já está atualizado, é um clique posterior, não
    // logo após o setSuccessInfo). Causa raiz do bug "avatar/nome vazio +
    // redirecionado pro ENEM logo após o autologin, resolvido só com
    // refresh" que o await checkAuth() dentro de enterPlatform evita:
    // escrever direto no localStorage NÃO avisa o AuthProvider principal,
    // que já está montado desde o carregamento inicial da página (fora do
    // <Router>, em App.tsx) com user=null (checkout é um fluxo anônimo).
    // Sem esperar checkAuth() antes de navegar, isAuthenticated ficava
    // travado em false pro resto da sessão mesmo com o token novo já no
    // localStorage — mesmo padrão já usado com sucesso em RyanLoginPage.tsx.
    const handleEnterPlatform = () => enterPlatform(successInfo?.accessToken, successInfo?.refreshToken);

    // Aba "Ainda não tenho conta" — mesma validação de senha de sempre, mais
    // uma última checagem de duplicata bem antes de submeter (cobre digitar/
    // colar um email sem nunca sair do campo, então sem disparar o blur).
    const handleSignupSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError(null);
        setSubmitError(null);
        if (!name.trim() || !whatsapp.trim() || !email.trim()) return;
        if (password.length < 6) { setPasswordError('A senha deve ter no mínimo 6 caracteres.'); return; }
        if (password !== confirmPassword) { setPasswordError('As senhas não coincidem.'); return; }
        if (!acceptedTerms) { setSubmitError('É necessário concordar com os Termos de Uso.'); return; }

        setCheckingEmail(true);
        const exists = await checkEmailExists(email.trim());
        setCheckingEmail(false);
        if (exists) { setDuplicateEmailNotice(true); return; }

        completeIdentification(name.trim(), email.trim(), whatsapp.trim(), false, password);
    };

    // Aba "Já tenho conta" — login de verdade (POST /api/auth/login, senha
    // confere), não mais uma checagem de existência de email. Depois de
    // autenticado, segue pro mesmo completeIdentification de sempre com
    // existingAccount:true (backend não exige nome/whatsapp nesse caminho —
    // um `name` vazio já cai no fallback "Cliente" já usado noutros lugares).
    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        if (!email.trim() || !password) return;
        setLoggingIn(true);
        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), password, platform: 'ryan' }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Email ou senha incorretos.');
            await completeIdentification(data.username || '', email.trim(), '', true);
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Erro de conexão. Tente novamente.');
        } finally {
            setLoggingIn(false);
        }
    };

    // Entrar/criar conta com Google — mesma rota usada em RyanLoginPage.tsx
    // (POST /api/auth/google, platform 'ryan'; cria a conta automaticamente
    // se o email ainda não existir). Depois de autenticado, segue pro mesmo
    // completeIdentification de sempre com existingAccount:true — a conta já
    // existe nesse ponto (recém-criada ou não), então não há senha pra
    // enviar, igual ao caminho "Já tenho conta" de handleLoginSubmit.
    const handleGoogleAuth = async (credentialResponse: CredentialResponse) => {
        setSubmitError(null);
        setGoogleAuthLoading(true);
        try {
            const res = await fetch('/api/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: credentialResponse.credential, platform: 'ryan' }),
            });
            const data = await res.json();
            if (!res.ok) {
                setSubmitError(data.message || 'Erro ao continuar com Google.');
                return;
            }
            await completeIdentification(data.username || '', data.email || email, '', true);
        } catch {
            setSubmitError('Erro de conexão. Tente novamente.');
        } finally {
            setGoogleAuthLoading(false);
        }
    };

    // "Não é você? Recomeçar" — único jeito explícito de abandonar um
    // progresso restaurado e voltar pra um checkout totalmente em branco
    // (Parte 2: limpar o localStorage "quando o aluno explicitamente
    // reiniciar o processo").
    const handleRestartCheckout = () => {
        if (slug) clearCheckoutProgress(slug);
        stopPolling();
        setAccessRequestId(null);
        setIdentificationDone(false);
        setRestoredNotice(false);
        setPaymentPhase('choose');
        setPixCharge(null);
        setChargeError(null);
        setFailureDetail(null);
        setSuccessInfo(null);
        setPollingTimedOut(false);
        setEmail('');
        setName('');
        setWhatsapp('');
        setPassword('');
        setConfirmPassword('');
        setPasswordError(null);
        setSubmitError(null);
        setDuplicateEmailNotice(false);
        setFinalName('');
        setFinalEmail('');
        setWasNewAccount(false);
        setIdentMode('signup');
        setAcceptedTerms(false);
    };

    const copyPix = async () => {
        if (!pixCharge?.qrCode) return;
        try {
            await navigator.clipboard.writeText(pixCharge.qrCode);
        } catch {
            const el = document.createElement('textarea');
            el.value = pixCharge.qrCode;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        }
        setCopiedPix(true);
        setTimeout(() => setCopiedPix(false), 2500);
    };

    // Agrupa as recomendações em linhas de 2 — linhas completas viram um par
    // lado a lado (grid 2x2); uma recomendação "sobrando" sozinha na última
    // linha (ou a única que existir) vira um card largo, com o conteúdo
    // organizado em duas colunas internas em vez de tudo empilhado.
    const recRows: Recommendation[][] = [];
    for (let i = 0; i < recommendations.length; i += 2) {
        recRows.push(recommendations.slice(i, i + 2));
    }

    const renderRecCard = (r: Recommendation, wide: boolean) => {
        const isSelected = selectedRecIds.has(r.productId);
        const hasDiscount = r.priceCents < r.basePriceCents;
        const itemSavings = r.basePriceCents - r.priceCents;
        const discountPct = hasDiscount ? Math.round((itemSavings / r.basePriceCents) * 100) : 0;

        const leftContent = (
            <>
                <div className="rcp-rec-title-row">
                    <h3 className="rcp-rec-title">{r.title}</h3>
                    {r.badge && <span className="rcp-rec-social-badge"><Award size={11} /> {r.badge}</span>}
                </div>
                {r.description && <p className="rcp-rec-desc">{r.description}</p>}
                <div className="rcp-rec-price-row">
                    {hasDiscount && <span className="rcp-rec-price-from">{formatPrice(r.basePriceCents)}</span>}
                    <span className="rcp-rec-price-to">{formatPrice(r.priceCents)}</span>
                </div>
                {hasDiscount && (
                    <p className="rcp-rec-savings">Você economiza {discountPct}% ({formatPrice(itemSavings)})</p>
                )}
            </>
        );

        const rightContent = (
            <>
                {r.highlights.length > 0 && (
                    <ul className="rcp-rec-highlights">
                        {r.highlights.map((h, i) => <li key={i}><Check size={13} /> {h}</li>)}
                    </ul>
                )}
                {r.publicSlug && (
                    <a className="rcp-rec-link" href={`/ryan/produto/${r.publicSlug}`} target="_blank" rel="noopener noreferrer">
                        Ver produto <ExternalLink size={12} />
                    </a>
                )}
                <div className="rcp-rec-actions">
                    {isSelected ? (
                        <>
                            <span className="rcp-rec-added-pill"><Check size={14} /> Adicionado</span>
                            <button type="button" className="rcp-rec-remove-btn"
                                onClick={() => toggleRec(r.productId)} disabled={identificationDone}>
                                <X size={13} /> Remover
                            </button>
                        </>
                    ) : (
                        <button type="button" className="rcp-rec-btn"
                            onClick={() => toggleRec(r.productId)} disabled={identificationDone}>
                            <Plus size={15} /> Adicionar ao pedido
                        </button>
                    )}
                </div>
            </>
        );

        return (
            <div key={r.productId}
                className={`rcp-rec-card${isSelected ? ' rcp-rec-card--added' : ''}${justAddedId === r.productId ? ' rcp-rec-card--pulse' : ''}${wide ? ' rcp-rec-card--wide' : ''}`}>
                {hasDiscount && <span className="rcp-rec-discount-ribbon">-{discountPct}%</span>}
                {wide ? (
                    <>
                        <div className="rcp-rec-wide-col">{leftContent}</div>
                        <div className="rcp-rec-wide-col rcp-rec-wide-col--right">{rightContent}</div>
                    </>
                ) : (
                    <>{leftContent}{rightContent}</>
                )}
            </div>
        );
    };

    // ── Sub-blocos reaproveitados entre a versão mobile (inline) e a sidebar
    //    sticky do desktop ──────────────────────────────────────────────

    const doubtsWhatsappUrl = product.publicPage.whatsappNumber
        ? buildWhatsappUrl(product.publicPage.whatsappNumber, product.title, `Olá! Tenho uma dúvida sobre "${product.title}" antes de finalizar a compra.`)
        : null;

    const orderSummary = (
        <div className={`rcp-summary-card${summaryPulse ? ' rcp-summary-card--pulse' : ''}`}>
            <h3 className="rcp-summary-title">Seu pedido</h3>
            <ul className="rcp-summary-items">
                <li className="rcp-summary-item">
                    <span><BookOpen size={13} /> {product.title}</span>
                    <span>{formatPrice(product.priceCents)}</span>
                </li>
                {selectedRecs.map(r => (
                    <li key={r.productId} className="rcp-summary-item rcp-reveal">
                        <span><BookOpen size={13} /> {r.title}</span>
                        <span>{formatPrice(r.priceCents)}</span>
                    </li>
                ))}
                {appliedCoupon && (
                    <li className="rcp-summary-item rcp-reveal rcp-summary-item--discount">
                        <span>Cupom {appliedCoupon.code}</span>
                        <span>-{formatPrice(appliedCoupon.discountCents)}</span>
                    </li>
                )}
            </ul>

            <div className="rcp-coupon-box">
                {appliedCoupon ? (
                    <div className="rcp-coupon-applied">
                        <span><Check size={13} /> Cupom <strong>{appliedCoupon.code}</strong> aplicado</span>
                        {!identificationDone && (
                            <button type="button" className="rcp-coupon-remove" onClick={removeCoupon}>
                                <X size={12} /> Remover
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="rcp-coupon-form">
                        <input
                            type="text" className="rcp-coupon-input" placeholder="Código do cupom"
                            value={couponInput} disabled={identificationDone || couponChecking}
                            onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(null); }}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                        />
                        <button
                            type="button" className="rcp-coupon-apply-btn"
                            onClick={applyCoupon} disabled={identificationDone || couponChecking || !couponInput.trim()}
                        >
                            {couponChecking ? 'Verificando...' : 'Aplicar'}
                        </button>
                    </div>
                )}
                {couponError && <p className="rcp-coupon-error">{couponError}</p>}
            </div>

            <div className="rcp-summary-total-row" key={runningTotal}>
                <span className="rcp-summary-total-label">Total</span>
                <span className="rcp-summary-total-value">{formatPrice(animatedTotal)}</span>
            </div>

            {runningSavings > 0 && (
                <div className="rcp-summary-savings" key={runningSavings}>
                    <Zap size={13} /> Você está economizando {formatPrice(runningSavings)}
                </div>
            )}

            {couponLostNotice && (
                <div className="rcp-coupon-lost-notice">
                    O cupom aplicado esgotou enquanto seu pagamento anterior era processado — sua compra segue normalmente, mas sem o desconto.
                </div>
            )}

            <div className="rcp-summary-trust">
                <span><CreditCard size={12} /> Pagamento seguro via Mercado Pago</span>
                <span><Clock size={12} /> Acesso válido até o fim do semestre letivo</span>
                <span><ShieldCheck size={12} /> Liberação assim que o pagamento for confirmado</span>
                {doubtsWhatsappUrl ? (
                    <a href={doubtsWhatsappUrl} target="_blank" rel="noopener noreferrer">
                        <MessageCircle size={12} /> Dúvidas? É só chamar no WhatsApp
                    </a>
                ) : (
                    <span><MessageCircle size={12} /> Dúvidas? É só chamar no WhatsApp</span>
                )}
            </div>
        </div>
    );

    const compactHowItWorks = (
        <div className="rcp-compact-how">
            <p className="rcp-compact-how-title">Como funciona</p>
            {HOW_IT_WORKS_STEPS.map((s, i) => (
                <div key={i} className="rcp-compact-how-step">
                    <span className="rpp-modal-next-num">{i + 1}</span>
                    <span>{s}</span>
                </div>
            ))}
        </div>
    );

    return (
        <MotionConfig reducedMotion="user">
        <div className="rcp-root">
            <RyanTestModeBadge />
            {/* Barra fixa só no mobile (a sidebar, que já mostra o passo a passo
                depois que o hero sai da tela, só existe a partir de 900px — ver
                .rcp-sidebar) — aparece ao rolar pra baixo, mostrando só o passo
                a passo, pra sempre saber em que etapa está sem precisar voltar
                pro topo. Instância própria do indicador, sem o layoutId
                compartilhado (STEPS_LAYOUT_ID) — aquele é só pra transição
                header↔sidebar; esta barra sempre existe, só troca de
                visível/escondida via CSS (translateY + transition). */}
            <div className={`rcp-mobile-steps-bar${!heroVisible ? ' rcp-mobile-steps-bar--visible' : ''}`}>
                <StepsIndicator getState={stepState} />
            </div>

            {/* ── HERO ── */}
            <header className="rcp-hero">
                <div className="rcp-hero-pattern" />
                <div className="rcp-hero-topline">
                    <button type="button" className="rcp-back-btn" onClick={() => navigate(`/ryan/produto/${slug}`)}>
                        <ArrowLeft size={15} /> <span>Voltar</span>
                    </button>

                    <div className="rcp-hero-center">
                        <p className="rcp-hero-eyebrow">Você está adquirindo</p>
                        <h1 className="rcp-hero-product">{product.title}</h1>
                    </div>

                    <img src="/Ryan/graia-logo-branca.png" alt="GRAIA Medicina" className="rcp-hero-logo" />
                </div>

                {/* O passo a passo mora aqui só enquanto o hero está visível — o
                    placeholder invisível reserva o espaço sempre (pra o header
                    nunca mudar de altura/pular), e o elemento real (com
                    layoutId) é quem efetivamente "viaja" pra sidebar quando
                    some daqui. */}
                <div className="rcp-hero-steps-slot">
                    <div className="rcp-steps-placeholder" aria-hidden="true">
                        <StepsIndicator getState={stepState} />
                    </div>
                    {heroVisible && (
                        <motion.div
                            layoutId={STEPS_LAYOUT_ID}
                            transition={STEPS_TRANSITION}
                            className="rcp-steps-shared-wrap"
                        >
                            <StepsIndicator getState={stepState} />
                        </motion.div>
                    )}
                </div>
                {/* Marcador invisível no fim do hero — quando ele sai da tela ao
                    rolar, o passo a passo "se muda" pro topo do resumo na sidebar. */}
                <div ref={heroSentinelRef} className="rcp-hero-sentinel" />
            </header>

            {/* Reserva o espaço exato que a barra fixa acima ocupa quando visível
                — mesmo truque do .rcp-steps-placeholder do header (conteúdo
                invisível, mas presente, decide a altura sozinho em vez de um
                número mágico), pra garantir que a barra nunca cubra conteúdo da
                página (só existe visualmente quando o hero já saiu da tela). */}
            <div className={`rcp-mobile-steps-spacer${!heroVisible ? ' rcp-mobile-steps-spacer--active' : ''}`} aria-hidden="true">
                <div className="rcp-mobile-steps-spacer-inner">
                    <StepsIndicator getState={stepState} />
                </div>
            </div>

            <div className="rcp-page">
                <div className="rcp-main-col">

                    {/* Progresso restaurado (Parte 2, revisado) — primeiro elemento
                        dentro da coluna principal, ANTES até da introdução do
                        produto, pra ser a primeira coisa visível sem precisar
                        rolar a página (Investigação item 2). Mensagem transparente
                        com o email restaurado, em vez de um texto genérico
                        (Investigação item 3) — nunca deixa o formulário aparecer
                        pré-preenchido sem dizer de quem é o dado nem como sair
                        disso. Visível em qualquer etapa (identificação ou já em
                        pagamento), já que "recomeçar" precisa valer nos dois. */}
                    {restoredNotice && (
                        <div className="rcp-restored-banner">
                            <span>
                                {email.trim()
                                    ? <>Continuando como <strong>{email.trim()}</strong> — não é você?</>
                                    : 'Continuando de onde você parou — não é você?'}
                            </span>
                            <button type="button" onClick={handleRestartCheckout}>Recomeçar do zero</button>
                        </div>
                    )}

                    {/* BLOCO: introdução do produto */}
                    <section className="rcp-card rcp-card--intro">
                        <div className="rcp-intro-text">
                            <h2 className="rcp-intro-title" style={{ fontFamily: getRyanHeroFontFamily(product.publicPage.heroTitleFont) }}>
                                {product.title}
                            </h2>
                            {product.publicPage.heroDescription && (
                                <p className="rcp-intro-desc">{product.publicPage.heroDescription}</p>
                            )}
                        </div>

                        {statCards.length > 0 && (
                            <div className="rcp-stat-row">
                                {statCards.map((c, i) => (
                                    <div key={i} className="rcp-stat-card">
                                        <span className="rcp-stat-icon"><c.icon size={26} /></span>
                                        <div className="rcp-stat-text">
                                            <span className="rcp-stat-value">{c.value.toLocaleString('pt-BR')}</span>
                                            <span className="rcp-stat-label">{c.label}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* RESUMO — só no mobile, entre introdução/upsell e identificação;
                        no desktop o resumo vive inteiro na sidebar sticky. */}
                    <div className="rcp-summary-mobile">
                        {orderSummary}
                    </div>

                    {/* BLOCO: recomendações — só existe se houver alguma */}
                    {recommendations.length > 0 && (
                        <section className="rcp-card rcp-card--highlight">
                            <div className="rcp-section-heading">
                                <h2><Sparkles size={17} /> Continue sua jornada no semestre</h2>
                                <p>Materiais que fazem parte das próximas semanas do seu semestre.</p>
                            </div>

                            <div className="rcp-rec-grid">
                                {recRows.map((row, i) => (
                                    <div key={i} className={row.length === 1 ? 'rcp-rec-row-single' : 'rcp-rec-row-pair'}>
                                        {row.map(r => renderRecCard(r, row.length === 1))}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* BLOCO: identificação — container próprio, elevado, sinaliza
                        claramente "agora é sua vez de preencher algo". Duas
                        abas explícitas em vez de detecção automática por
                        email (Parte 3): a pessoa diz de cara se já tem conta
                        ou não, sem depender do sistema adivinhar certo. */}
                    <section className="rcp-card rcp-card--ident" id="rcp-ident">
                        {!identificationDone ? (
                            <>
                                <div className="rcp-ident-header">
                                    <span className="rcp-ident-icon"><UserCheck size={20} /></span>
                                    <h2 className="rcp-ident-title">Vamos te identificar</h2>
                                </div>

                                {sessionAccount ? (
                                    <div className="rcp-ident-form">
                                        <p className="rcp-session-text">
                                            Você está logado como <strong>{sessionAccount.username || 'você'}</strong> ({sessionAccount.email}).
                                            O acesso comprado será liberado nesta conta.
                                        </p>

                                        {submitError && <p className="rpp-modal-error"><AlertCircle size={13} /> {submitError}</p>}

                                        <button type="button" className="rpp-modal-submit" disabled={submitting}
                                            onClick={() => completeIdentification(sessionAccount.username || '', sessionAccount.email, '', true)}>
                                            {submitting ? 'Confirmando...' : 'Continuar com esta conta'}
                                        </button>

                                        <button type="button" className="rcp-session-switch"
                                            onClick={() => { setUseOtherAccount(true); setIdentMode('login'); setSubmitError(null); }}>
                                            Usar outra conta
                                        </button>
                                    </div>
                                ) : (
                                <>
                                <div className="rcp-ident-tabs">
                                    <button type="button"
                                        className={`rcp-ident-tab${identMode === 'signup' ? ' rcp-ident-tab--active' : ''}`}
                                        onClick={() => { setIdentMode('signup'); setSubmitError(null); setDuplicateEmailNotice(false); }}>
                                        Ainda não tenho conta
                                    </button>
                                    <button type="button"
                                        className={`rcp-ident-tab${identMode === 'login' ? ' rcp-ident-tab--active' : ''}`}
                                        onClick={() => { setIdentMode('login'); setSubmitError(null); setDuplicateEmailNotice(false); }}>
                                        Já tenho conta
                                    </button>
                                </div>

                                {identMode === 'signup' ? (
                                    <form className="rcp-ident-form" onSubmit={handleSignupSubmit}>
                                        <label className="rpp-modal-label" htmlFor="rcp-name">Nome</label>
                                        <input id="rcp-name" className="rpp-modal-input" value={name}
                                            onChange={e => setName(e.target.value)} required placeholder="Seu nome completo" autoFocus />

                                        <label className="rpp-modal-label" htmlFor="rcp-email">Email</label>
                                        <input id="rcp-email" className="rpp-modal-input" type="email" value={email}
                                            onChange={e => { setEmail(e.target.value); setDuplicateEmailNotice(false); }}
                                            onBlur={handleSignupEmailBlur}
                                            required placeholder="seu@email.com" />
                                        {checkingEmail && <span className="rcp-checking-email">Verificando...</span>}

                                        {duplicateEmailNotice ? (
                                            <div className="rcp-duplicate-notice" ref={identRevealRef}>
                                                <AlertCircle size={14} />
                                                <div>
                                                    <p>Já existe uma conta com esse email.</p>
                                                    <button type="button" className="rpp-modal-secondary-link" onClick={switchToLogin}>
                                                        Ir para "Já tenho conta"
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <label className="rpp-modal-label" htmlFor="rcp-whatsapp">WhatsApp</label>
                                                <input id="rcp-whatsapp" className="rpp-modal-input" value={whatsapp}
                                                    onChange={e => setWhatsapp(e.target.value)} required placeholder="(11) 91234-5678" />

                                                <label className="rpp-modal-label" htmlFor="rcp-password">Senha</label>
                                                <input id="rcp-password" className="rpp-modal-input" type="password" value={password}
                                                    onChange={e => setPassword(e.target.value)} required placeholder="Mínimo 6 caracteres" />

                                                <label className="rpp-modal-label" htmlFor="rcp-confirm-password">Confirmar senha</label>
                                                <input id="rcp-confirm-password" className="rpp-modal-input" type="password" value={confirmPassword}
                                                    onChange={e => setConfirmPassword(e.target.value)} required placeholder="Repita a senha" />

                                                <div className="rcp-terms-field">
                                                    <input
                                                        type="checkbox"
                                                        id="rcp-terms"
                                                        className="rcp-terms-checkbox"
                                                        checked={acceptedTerms}
                                                        onChange={e => setAcceptedTerms(e.target.checked)}
                                                        disabled={submitting || googleAuthLoading}
                                                    />
                                                    <label htmlFor="rcp-terms" className="rcp-terms-label">
                                                        Li e concordo com os{' '}
                                                        <Link to="/ryan/termos" target="_blank" rel="noopener noreferrer">Termos de Uso</Link>
                                                        {' '}e a Política de Privacidade
                                                    </label>
                                                </div>

                                                {passwordError && <p className="rpp-modal-error"><AlertCircle size={13} /> {passwordError}</p>}
                                                {submitError && <p className="rpp-modal-error"><AlertCircle size={13} /> {submitError}</p>}

                                                <button type="submit" className="rpp-modal-submit" disabled={submitting || checkingEmail || !acceptedTerms}>
                                                    {submitting ? 'Criando conta...' : 'Criar conta e continuar'}
                                                </button>

                                                <div className="rcp-google-divider"><span>ou</span></div>

                                                <div className={`rcp-google-wrap${!acceptedTerms ? ' rcp-google-wrap--disabled' : ''}`}>
                                                    <GoogleLoginButton
                                                        onSuccess={handleGoogleAuth}
                                                        onError={() => setSubmitError('Erro ao criar conta com Google.')}
                                                    />
                                                </div>
                                                {!acceptedTerms && (
                                                    <p className="rcp-google-hint">Marque a caixa acima para continuar com o Google</p>
                                                )}
                                                {googleAuthLoading && <p className="rcp-google-hint">Criando conta...</p>}
                                            </>
                                        )}
                                    </form>
                                ) : (
                                    <form className="rcp-ident-form" onSubmit={handleLoginSubmit}>
                                        <label className="rpp-modal-label" htmlFor="rcp-login-email">Email</label>
                                        <input id="rcp-login-email" className="rpp-modal-input" type="email" value={email}
                                            onChange={e => setEmail(e.target.value)} required placeholder="seu@email.com" autoFocus />

                                        <label className="rpp-modal-label" htmlFor="rcp-login-password">Senha</label>
                                        <input id="rcp-login-password" className="rpp-modal-input" type="password" value={password}
                                            onChange={e => setPassword(e.target.value)} required placeholder="Sua senha" />

                                        {submitError && <p className="rpp-modal-error"><AlertCircle size={13} /> {submitError}</p>}

                                        <button type="submit" className="rpp-modal-submit" disabled={loggingIn || submitting}>
                                            {loggingIn ? 'Entrando...' : submitting ? 'Confirmando...' : 'Entrar e continuar'}
                                        </button>

                                        <div className="rcp-google-divider"><span>ou</span></div>

                                        <div className="rcp-google-wrap">
                                            <GoogleLoginButton
                                                onSuccess={handleGoogleAuth}
                                                onError={() => setSubmitError('Erro ao entrar com Google.')}
                                            />
                                        </div>
                                        {googleAuthLoading && <p className="rcp-google-hint">Entrando...</p>}
                                    </form>
                                )}
                                </>
                                )}
                            </>
                        ) : (
                            <div className="rcp-reveal rcp-ident-done">
                                <CheckCircle size={16} />
                                <div className="rcp-ident-done-text">
                                    <strong>{wasNewAccount ? 'Sua conta foi criada!' : 'Login confirmado!'}</strong>
                                    <span>{finalEmail || email}</span>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* BLOCO: pagamento — a etapa mais importante da jornada.
                        Seis fases possíveis (paymentPhase): choose (Pix ou
                        cartão) → pix (QR + polling) ou card (Brick) →
                        confirming (só cartão, aguardando webhook) → success
                        ou failed. Nunca mostra dado de pagamento manual
                        (chave Pix fixa/comprovante) — isso continua existindo
                        só no backend, pro admin usar por fora se quiser. */}
                    {identificationDone && (
                        <section className="rcp-card rcp-card--payment rcp-reveal" ref={paymentRef}>
                            <div className="rcp-payment-glow" />

                            {paymentPhase === 'choose' && (
                                <div className="rcp-reveal-stagger">
                                    <div className="rcp-section-heading rcp-section-heading--dark">
                                        <h2>Como você quer pagar?</h2>
                                        <p>Escolha Pix ou cartão — seu acesso é liberado automaticamente assim que confirmarmos.</p>
                                    </div>
                                    <p className="rcp-payment-price">{formatPrice(runningTotal)}</p>
                                    <div className="rcp-method-grid">
                                        <button type="button" className="rcp-method-card" onClick={choosePix}>
                                            <QrCode size={26} />
                                            <span className="rcp-method-title">Pix</span>
                                            <span className="rcp-method-desc">Aprovação em segundos</span>
                                        </button>
                                        <button type="button" className="rcp-method-card" onClick={chooseCard}>
                                            <CreditCard size={26} />
                                            <span className="rcp-method-title">Cartão de crédito</span>
                                            <span className="rcp-method-desc">Aprovação imediata</span>
                                        </button>
                                    </div>
                                    {chargeError && <p className="rpp-modal-error"><AlertCircle size={13} /> {chargeError}</p>}
                                </div>
                            )}

                            {paymentPhase === 'pix' && (
                                <div className="rcp-reveal-stagger">
                                    <div className="rcp-section-heading rcp-section-heading--dark">
                                        <h2>Pague com Pix</h2>
                                        <p>Escaneie o QR code ou copie o código abaixo no app do seu banco.</p>
                                    </div>
                                    <p className="rcp-payment-price">{formatPrice(runningTotal)}</p>

                                    {chargeCreating && (
                                        <div className="rcp-payment-loading">
                                            <Loader2 size={22} className="rcp-spin" />
                                            <span>Gerando o Pix...</span>
                                        </div>
                                    )}

                                    {!chargeCreating && pixCharge && (
                                        <>
                                            <div className="rcp-payment-grid">
                                                {pixCharge.qrCodeBase64 && (
                                                    <div className="rcp-pix-qr-wrap">
                                                        <img src={`data:image/png;base64,${pixCharge.qrCodeBase64}`} alt="QR Code para pagamento Pix" className="rcp-pix-qr" />
                                                        <span className="rcp-pix-qr-label">Escaneie no app do seu banco</span>
                                                    </div>
                                                )}
                                                {pixCharge.qrCode && (
                                                    <div className="rcp-pix-block">
                                                        <div className="rcp-pix-header">
                                                            <QrCode size={14} />
                                                            <span>Pix copia e cola</span>
                                                        </div>
                                                        <div className="rcp-pix-key-row">
                                                            <span className="rcp-pix-key">{pixCharge.qrCode}</span>
                                                        </div>
                                                        <button type="button"
                                                            className={`rcp-pix-copy ${copiedPix ? 'rcp-pix-copy--done' : ''}`}
                                                            onClick={copyPix}>
                                                            {copiedPix ? <><CheckCircle size={14} /> Copiado!</> : <><Copy size={14} /> Copiar código</>}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {!pollingTimedOut && (
                                                <div className="rcp-waiting-note">
                                                    <Loader2 size={15} className="rcp-spin" />
                                                    <span>Assim que o pagamento for confirmado (geralmente em poucos segundos), seu acesso é liberado automaticamente aqui nesta tela.</span>
                                                </div>
                                            )}

                                            {pollingTimedOut && (
                                                <div className="rcp-timeout-note">
                                                    <p>Está demorando mais que o esperado — isso pode acontecer, seu pagamento não foi perdido.</p>
                                                    <div className="rcp-timeout-actions">
                                                        <button type="button" className="rpp-modal-secondary-link" onClick={handleManualCheck} disabled={checkingNow}>
                                                            {checkingNow ? 'Verificando...' : 'Verificar agora'}
                                                        </button>
                                                        {doubtsWhatsappUrl && (
                                                            <a href={doubtsWhatsappUrl} target="_blank" rel="noopener noreferrer" className="rpp-modal-secondary-link">
                                                                <WhatsAppIcon size={13} /> Falar no WhatsApp
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {!chargeCreating && chargeError && <p className="rpp-modal-error"><AlertCircle size={13} /> {chargeError}</p>}

                                    <button type="button" className="rcp-switch-method-link" onClick={backToChoice}>Trocar forma de pagamento</button>
                                </div>
                            )}

                            {paymentPhase === 'card' && (
                                <div className="rcp-reveal-stagger">
                                    <div className="rcp-section-heading rcp-section-heading--dark">
                                        <h2>Pague com cartão</h2>
                                        <p>Seus dados são protegidos e processados diretamente pelo Mercado Pago.</p>
                                    </div>
                                    <p className="rcp-payment-price">{formatPrice(runningTotal)}</p>

                                    <RyanCardPaymentBrick
                                        key={cardBrickKey}
                                        amountCents={runningTotal}
                                        payerEmail={email.trim()}
                                        onSubmit={handleCardSubmit}
                                        onError={() => setChargeError('Erro ao carregar formulário. Tente novamente.')}
                                    />

                                    {chargeError && <p className="rpp-modal-error"><AlertCircle size={13} /> {chargeError}</p>}

                                    <button type="button" className="rcp-switch-method-link" onClick={backToChoice}>Trocar forma de pagamento</button>
                                </div>
                            )}

                            {paymentPhase === 'confirming' && (
                                <div className="rcp-reveal-stagger rcp-confirming">
                                    <Loader2 size={32} className="rcp-spin" />
                                    <p className="rcp-confirming-text">Confirmando seu pagamento...</p>
                                    {pollingTimedOut && (
                                        <div className="rcp-timeout-note">
                                            <p>Está demorando mais que o esperado — isso pode acontecer, seu pagamento não foi perdido.</p>
                                            <div className="rcp-timeout-actions">
                                                <button type="button" className="rpp-modal-secondary-link" onClick={handleManualCheck} disabled={checkingNow}>
                                                    {checkingNow ? 'Verificando...' : 'Verificar agora'}
                                                </button>
                                                {doubtsWhatsappUrl && (
                                                    <a href={doubtsWhatsappUrl} target="_blank" rel="noopener noreferrer" className="rpp-modal-secondary-link">
                                                        <WhatsAppIcon size={13} /> Falar no WhatsApp
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {paymentPhase === 'success' && successInfo && (
                                <div className="rcp-reveal-stagger rcp-payment-result">
                                    <div className="rcp-result-icon rcp-result-icon--success"><CheckCircle size={38} /></div>
                                    <h2>{finalName ? `Pagamento confirmado, ${finalName.split(' ')[0]}!` : 'Pagamento confirmado!'}</h2>
                                    <p>Seu acesso a <strong>{successInfo.productTitle || product.title}</strong> já está liberado.</p>
                                    <button type="button" className="rpp-modal-whatsapp-btn rcp-payment-cta" onClick={handleEnterPlatform}>
                                        Entrar na plataforma →
                                    </button>
                                </div>
                            )}

                            {paymentPhase === 'failed' && (
                                <div className="rcp-reveal-stagger rcp-payment-result">
                                    <div className="rcp-result-icon rcp-result-icon--failed"><AlertCircle size={38} /></div>
                                    <h2>Pagamento não aprovado</h2>
                                    <p>{failureDetail || 'Não conseguimos confirmar seu pagamento.'}</p>
                                    <button type="button" className="rcp-payment-retry-btn" onClick={handleRetryPayment}>Tentar novamente</button>
                                </div>
                            )}
                        </section>
                    )}

                    {/* "Como funciona" — só no mobile (no desktop essa
                        informação já está na sidebar, versão compacta). */}
                    <div className="rcp-next-mobile-only">
                        <div className="rpp-modal-next-box rcp-next-box">
                            <p className="rpp-modal-next-title">Como funciona:</p>
                            <ol className="rpp-modal-next-list">
                                {HOW_IT_WORKS_STEPS.map((s, i) => (
                                    <li key={i}><span className="rpp-modal-next-num">{i + 1}</span> {s}</li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>

                {/* SIDEBAR (desktop) — resumo sticky + versão compacta de como funciona.
                    O passo a passo "chega" aqui (com uma transição coordenada) assim
                    que o do header sai da tela ao rolar, e "some" de volta assim que
                    o header reaparece — mesmo componente dos dois lados. */}
                <aside className="rcp-sidebar">
                    {/* `layout` nestes dois: quando o passo a passo chega aqui (ou
                        vai embora), a altura do slot e a posição do card de resumo
                        logo abaixo são recalculadas com uma animação de layout —
                        o resumo nunca "pula", ele abre/fecha espaço suavemente. */}
                    <motion.div layout transition={STEPS_TRANSITION} className="rcp-sidebar-steps-slot">
                        {!heroVisible && (
                            <motion.div
                                layoutId={STEPS_LAYOUT_ID}
                                transition={STEPS_TRANSITION}
                                className="rcp-steps-shared-wrap rcp-steps-shared-wrap--sidebar"
                            >
                                <StepsIndicator getState={stepState} />
                            </motion.div>
                        )}
                    </motion.div>
                    <motion.div layout transition={STEPS_TRANSITION}
                        className={`rcp-sidebar-summary-wrap${!heroVisible ? ' rcp-sidebar-summary-wrap--merged' : ''}`}>
                        {orderSummary}
                    </motion.div>
                    <div className="rcp-sidebar-compact-how">
                        {compactHowItWorks}
                    </div>
                </aside>
            </div>
        </div>
        </MotionConfig>
    );
};

export default RyanCheckoutPage;
